import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';

// Home's daily "took your stack?" confirm (SS2). One tap records the day's
// adherence (yes / mostly / didn't / skip). A "yes" or "mostly" auto-logs a
// supplement intake entry per active item in your stack — no retyping — and marks
// the supplements domain complete for the day. "Mostly" reveals the stack so you
// can uncheck what you missed. Forgetting is fine: "didn't" and "skip" log
// nothing and never count against you.

const todayStr = () => new Date().toISOString().split('T')[0];

const OPTIONS = [
  { status: 'yes', label: 'Took it all' },
  { status: 'mostly', label: 'Mostly' },
  { status: 'no', label: "Didn't" },
  { status: 'skipped', label: 'Skip' },
];

const StackConfirmCard = ({ onEntrySaved }) => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [excluded, setExcluded] = useState([]); // regimen ids skipped (for "mostly")
  const [showStack, setShowStack] = useState(false);
  const day = todayStr();

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/stack-confirm', { params: { target_date: day } });
      setData(res.data);
      setExcluded(res.data?.excluded_items || []);
      setShowStack(res.data?.status === 'mostly');
    } catch {
      // Silent — the card just doesn't render if the fetch fails.
    }
  }, [day]);

  useEffect(() => { load(); }, [load]);

  const confirm = async (status, excludedItems) => {
    setSaving(true);
    try {
      const res = await apiClient.put('/api/stack-confirm', {
        status,
        date: day,
        excluded_items: excludedItems || [],
      });
      setData(res.data);
      setExcluded(res.data?.excluded_items || []);
      if (res.data?.generated_entries && onEntrySaved) onEntrySaved();
    } catch {
      toast.error('Could not save that just now');
    } finally {
      setSaving(false);
    }
  };

  const onPick = (status) => {
    if (status === 'mostly') {
      // Reveal the stack to uncheck what was missed; confirm with the current set.
      setShowStack(true);
      confirm('mostly', excluded);
    } else {
      setShowStack(false);
      confirm(status, []);
    }
  };

  const toggleExcluded = (id) => {
    const next = excluded.includes(id) ? excluded.filter((x) => x !== id) : [...excluded, id];
    setExcluded(next);
    confirm('mostly', next);
  };

  // Don't render until we know there's a stack to confirm — no empty nag.
  if (!data) return null;
  const supplements = data.supplements || [];
  if (supplements.length === 0) return null;

  const status = data.status;
  const takenCount = supplements.filter((s) => !excluded.includes(s.id)).length;

  return (
    <div className="rounded-2xl border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card p-5">
      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">Took your stack?</h2>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {status === 'yes' || status === 'mostly'
            ? `Logged ${takenCount} ${takenCount === 1 ? 'supplement' : 'supplements'} for today.`
            : 'One tap logs your whole stack. Forgetting is fine — “didn’t” and “skip” never count against you.'}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {OPTIONS.map((o) => {
          const active = status === o.status;
          return (
            <button
              key={o.status}
              type="button"
              onClick={() => onPick(o.status)}
              disabled={saving}
              className={`text-sm font-semibold px-3.5 py-2 rounded-lg border transition-colors disabled:opacity-50 ${
                active
                  ? 'bg-primary-600 border-primary-600 text-white'
                  : 'border-surface-border dark:border-surface-dark-border text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-surface-dark-raised/50'
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>

      {(showStack || status === 'mostly') && (
        <div className="mt-4">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
            Uncheck anything you skipped today:
          </p>
          <div className="flex flex-wrap gap-2">
            {supplements.map((s) => {
              const taken = !excluded.includes(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => toggleExcluded(s.id)}
                  disabled={saving}
                  className={`text-sm px-2.5 py-1 rounded-full border transition-colors disabled:opacity-70 ${
                    taken
                      ? 'bg-good/15 border-good/40 text-good-strong dark:text-good'
                      : 'border-surface-border dark:border-surface-dark-border text-gray-400 line-through'
                  }`}
                >
                  {taken ? '✓ ' : ''}{s.name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default StackConfirmCard;
